import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Link } from 'react-router-dom';

import './stylesheet.css';

import * as orgActions from '../../actions/org';

import {
  backlinkCounts,
  backlinksFor,
  databaseStats,
  DEFAULT_ROAM_DIRECTORY,
  findNodes,
} from '../../lib/org_roam';
import {
  clearDatabase,
  databaseSizeBytes,
  loadDatabase,
  saveDatabase,
} from '../../lib/org_roam_db';
import { syncRoamDatabase } from '../../lib/org_roam_sync';
import { filePathToUrl } from '../../lib/dashboard';

// How many results the finder shows. org-roam's own completion is unbounded
// because Emacs completion is; a scrolling list of 2,000 nodes in a phone
// browser is not the same thing.
const RESULTS_SHOWN = 40;

const formatBytes = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} kB`;

const formatSyncedAt = (millis) => {
  if (!millis) return 'never';
  try {
    return new Date(millis).toLocaleString();
  } catch (error) {
    return 'unknown';
  }
};

const OrgRoam = ({ syncBackendClient, roamDirectory, org }) => {
  const directory = (roamDirectory || '').trim() || DEFAULT_ROAM_DIRECTORY;

  const [database, setDatabase] = useState(loadDatabase);
  const [query, setQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [errors, setErrors] = useState([]);
  const [storageError, setStorageError] = useState(null);
  // Which node's backlinks are open. One at a time: the list is a finder, and
  // several expanded nodes at once turn it into a wall.
  const [expanded, setExpanded] = useState(null);

  // So a sync that is still running when the page unmounts stops writing state.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const stats = useMemo(() => databaseStats(database), [database]);
  // One pass over the links rather than a scan per row.
  const counts = useMemo(() => backlinkCounts(database), [database]);
  const results = useMemo(() => findNodes(database, query, RESULTS_SHOWN), [database, query]);
  const total = useMemo(() => findNodes(database, query, Number.MAX_SAFE_INTEGER).length, [
    database,
    query,
  ]);

  const handleSync = useCallback(async () => {
    if (!syncBackendClient || syncing) return;

    setSyncing(true);
    setErrors([]);
    setStorageError(null);
    setProgress({ phase: 'listing' });

    const { database: next, errors: syncErrors } = await syncRoamDatabase(syncBackendClient, {
      directory,
      onProgress: (update) => mounted.current && setProgress(update),
    });

    if (!mounted.current) return;

    if (next) {
      const { error } = saveDatabase(next);
      setDatabase(next);
      setStorageError(error);
    }

    setErrors(syncErrors);
    setProgress(null);
    setSyncing(false);
  }, [syncBackendClient, syncing, directory]);

  const handleClear = () => {
    setDatabase(clearDatabase());
    setErrors([]);
    setStorageError(null);
  };

  const header = (
    <div className="roam__header">
      <h3 className="roam__title">org-roam</h3>
      <div className="roam__header__actions">
        <Link className="roam__header__action" to="/dashboard">
          <i className="fas fa-th-large" /> Dashboard
        </Link>
        <Link className="roam__header__action" to="/files/">
          <i className="fas fa-folder-open" /> Files
        </Link>
      </div>
    </div>
  );

  if (!syncBackendClient) {
    return (
      <div className="roam-container">
        {header}
        <div className="roam__error">Not connected to a sync backend.</div>
      </div>
    );
  }

  return (
    <div className="roam-container">
      {header}

      <div className="roam__intro">
        An index of every node in <code>{directory}</code> — a node being the file itself, or any
        headline, carrying an <code>:ID:</code> property. The index is built by reading those files
        once and is stored in this browser, not written back into your notes.
      </div>

      {/* ── The index ───────────────────────────────────────────────────────── */}

      <div className="roam__section-title">
        <span>The index</span>
        <span className="roam__section-title__note">synced {formatSyncedAt(stats.syncedAt)}</span>
      </div>

      <div className="roam__stats">
        <span>
          <strong>{stats.nodes}</strong> nodes
        </span>
        <span>
          <strong>{stats.files}</strong> files
        </span>
        <span>
          <strong>{stats.links}</strong> links
        </span>
        {stats.dangling > 0 && (
          <span
            className="roam__stats__dangling"
            title="Links pointing at an id the index does not have — not synced yet, or the note is gone."
          >
            <strong>{stats.dangling}</strong> dangling
          </span>
        )}
        <span className="roam__stats__size">{formatBytes(databaseSizeBytes(database))}</span>
      </div>

      <div className="roam__actions">
        <button className="roam__action" disabled={syncing} onClick={handleSync}>
          {syncing ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Syncing...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt" /> Sync the index
            </>
          )}
        </button>
        {!syncing && stats.nodes > 0 && (
          <button className="btn btn-passive" onClick={handleClear}>
            Clear
          </button>
        )}
        {!!progress && (
          <span className="roam__progress">
            {progress.phase === 'listing'
              ? `Listing files... ${progress.files || 0} found`
              : `Reading ${progress.done} of ${progress.total}`}
          </span>
        )}
      </div>

      {!!storageError && <div className="roam__error">{storageError}</div>}

      {errors.length > 0 && (
        <div className="roam__errors">
          <div className="roam__errors__title">
            {errors.length} {errors.length === 1 ? 'problem' : 'problems'} during the sync — the
            rest of the index is fine
          </div>
          <ul>
            {errors.slice(0, 10).map((error) => (
              <li key={`${error.path}-${error.message}`}>
                <code>{error.path}</code> — {error.message}
              </li>
            ))}
          </ul>
          {errors.length > 10 && <div>...and {errors.length - 10} more.</div>}
        </div>
      )}

      {/* ── Find a node ─────────────────────────────────────────────────────── */}

      <div className="roam__section-title">
        <span>Find a node</span>
        <span className="roam__section-title__note">
          {total > results.length ? `${results.length} of ${total}` : `${total} matching`}
        </span>
      </div>

      <input
        className="textfield roam__search"
        type="text"
        value={query}
        placeholder="Search by title or alias"
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
      />

      {stats.nodes === 0 ? (
        <div className="roam__empty">
          Nothing indexed yet. Press <em>Sync the index</em> above.
        </div>
      ) : results.length === 0 ? (
        <div className="roam__empty">Nothing matches “{query}”.</div>
      ) : (
        <ul className="roam__results">
          {results.map((node) => (
            <li className="roam__result" key={node.id}>
              <Link
                className="roam__result__link"
                to={filePathToUrl(node.file)}
                onClick={() => org.setPendingRoamTarget(node.id, node.file)}
              >
                <span className="roam__result__title">{node.title}</span>
                {node.tags.map((tag) => (
                  <span className="roam__tag" key={tag}>
                    {tag}
                  </span>
                ))}
              </Link>
              <div className="roam__result__meta">
                {node.olp.length > 0 && (
                  <span className="roam__result__olp">{node.olp.join(' › ')} › </span>
                )}
                <code>{node.file}</code>
                {node.aliases.length > 0 && (
                  <span className="roam__result__aliases">
                    {' '}
                    · also “{node.aliases.join('”, “')}”
                  </span>
                )}
              </div>

              {/* What links here. Collapsed by default: the finder's job is to
                  find, and expanding every node's backlinks would bury it. */}
              {!!counts[node.id] && (
                <button
                  className="roam__backlinks__toggle"
                  onClick={() => setExpanded(expanded === node.id ? null : node.id)}
                >
                  <i className={`fas fa-caret-${expanded === node.id ? 'down' : 'right'}`} />{' '}
                  {counts[node.id]} {counts[node.id] === 1 ? 'note links here' : 'notes link here'}
                </button>
              )}

              {expanded === node.id && (
                <ul className="roam__backlinks">
                  {backlinksFor(database, node.id).map((link, index) => (
                    <li className="roam__backlink" key={`${link.file}-${link.line}-${index}`}>
                      {link.sourceNode ? (
                        <Link
                          className="roam__backlink__source"
                          to={filePathToUrl(link.sourceNode.file)}
                          onClick={() =>
                            org.setPendingRoamTarget(link.sourceNode.id, link.sourceNode.file)
                          }
                        >
                          {link.sourceNode.title}
                        </Link>
                      ) : (
                        // A link written above the first node in its file has no
                        // node to belong to, so name the file instead of dropping
                        // the backlink.
                        <span className="roam__backlink__source roam__backlink__source--unknown">
                          {link.file}
                        </span>
                      )}
                      {!!link.context && (
                        <div className="roam__backlink__context">{link.context}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="roam__footer">
        Stored in this browser only, under <code>orgRoamDatabase</code> — org-roam keeps a SQLite
        file beside the notes, but writing a derived cache into a synced notes folder would push it
        to every device that shares the folder. Re-sync after editing notes elsewhere.
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  roamDirectory: state.base.get('roamDirectory') || DEFAULT_ROAM_DIRECTORY,
});

const mapDispatchToProps = (dispatch) => ({
  org: bindActionCreators(orgActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrgRoam);
